import {
	Box,
	Heading,
	SimpleGrid,
	Stat,
	StatLabel,
	StatNumber,
	Select,
	useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { getEstadisticasPorAgencia } from '../../services/estadisticas';
import {
	ResponsiveContainer,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	CartesianGrid,
	AreaChart,
	Area,
} from 'recharts';

const etiquetasContacto = {
	contacto_0_90: 'Último contacto: 0–90 días',
	contacto_91_120: 'Último contacto: 91–120 días',
	contacto_120_plus: 'Último contacto: > 120 días',
	sin_contacto: 'Sin contacto',
};

const etiquetasNoticias = {
	nuevas: 'Nuevas',
	contacto_30_90: 'Último contacto: 31–90 días',
	contacto_90_plus: 'Último contacto: > 90 días',
};

const EstadisticasPorAgencia = ({ filters }) => {
	const [data, setData] = useState([]);
	const [selectedUuid, setSelectedUuid] = useState('');
	const [agencia, setAgencia] = useState(null);
	const toast = useToast();

	useEffect(() => {
		const fetchData = async () => {
			try {
				const params = {};
				if (filters?.ultimosDias)
					params.ultimosDias = filters.ultimosDias;
				const resultados = await getEstadisticasPorAgencia(params);
				setData(resultados);
				if (resultados.length > 0) {
					setSelectedUuid(resultados[0].uuid);
				}
			} catch (error) {
				toast({
					title: 'Error al cargar estadísticas por agencia',
					status: 'error',
					isClosable: true,
				});
			}
		};

		fetchData();
	}, [filters?.ultimosDias, toast]);

	useEffect(() => {
		const seleccionada = data.find(d => d.uuid === selectedUuid);
		setAgencia(seleccionada || null);
	}, [selectedUuid, data]);

	if (!agencia) return null;

	return (
		<Box
			mt={8}
			w='100%'
			border='1px'
			borderColor='blue.400'
			rounded='lg'
			shadow='lg'
			p={4}
		>
			<Heading size='md' mb={4}>
				Estadísticas por agencia
			</Heading>

			<Select
				placeholder='Selecciona una agencia'
				value={selectedUuid}
				onChange={e => setSelectedUuid(e.target.value)}
				mb={4}
			>
				{data.map(d => (
					<option key={d.uuid} value={d.uuid}>
						{d.nombre}
					</option>
				))}
			</Select>

			<SimpleGrid columns={[1, 2, 3]} spacing={6}>
				{Object.entries(agencia.totales).map(([key, val]) => (
					<Stat key={key} p={4} borderWidth='1px' rounded='md'>
						<StatNumber fontSize='lg'>{val}</StatNumber>
						<StatLabel
							fontWeight='bold'
							textTransform='capitalize'
							color='blue.600'
							fontSize='sm'
						>
							{key}
						</StatLabel>
					</Stat>
				))}
			</SimpleGrid>

			{/* Actividades por tipo */}
			<Box mt={6}>
				<Heading size='sm' mb={2}>
					Actividades por tipo
				</Heading>
				<ResponsiveContainer width='100%' height={300}>
					<BarChart data={agencia.actividadesPorTipo}>
						<CartesianGrid strokeDasharray='3 3' />
						<XAxis dataKey='tipo' />
						<YAxis />
						<Tooltip />
						<Bar dataKey='total' fill='#3182CE' />
					</BarChart>
				</ResponsiveContainer>
			</Box>

			{/* Evolución mensual */}
			<Box mt={6}>
				<Heading size='sm' mb={2}>
					Evolución mensual de actividades
				</Heading>
				<ResponsiveContainer width='100%' height={300}>
					<AreaChart data={agencia.actividadesMensuales}>
						<CartesianGrid strokeDasharray='3 3' />
						<XAxis dataKey='mes' />
						<YAxis />
						<Tooltip />
						<Area
							type='monotone'
							dataKey='total'
							stroke='#2F855A'
							fill='#C6F6D5'
						/>
					</AreaChart>
				</ResponsiveContainer>
			</Box>

			{/* Inmuebles por contacto */}
			<Box mt={6}>
				<Heading size='sm' mb={2}>
					Inmuebles por contacto
				</Heading>
				<SimpleGrid columns={[1, 2, 4]} spacing={4}>
					{Object.entries(agencia.inmueblesPorFranjaContacto).map(
						([key, val]) => (
							<Stat
								key={key}
								p={4}
								borderWidth='1px'
								rounded='md'
							>
								<StatNumber fontSize='lg'>{val}</StatNumber>
								<StatLabel fontSize='sm'>
									{etiquetasContacto[key] || key}
								</StatLabel>
							</Stat>
						),
					)}
				</SimpleGrid>
			</Box>

			{/* Noticias activas */}
			<Box mt={6}>
				<Heading size='sm' mb={2}>
					Noticias activas
				</Heading>
				<SimpleGrid columns={[1, 2, 3]} spacing={4}>
					{Object.entries(agencia.noticiasActivas).map(
						([key, val]) => (
							<Stat
								key={key}
								p={4}
								borderWidth='1px'
								rounded='md'
							>
								<StatNumber fontSize='lg'>{val}</StatNumber>
								<StatLabel fontSize='sm'>
									{etiquetasNoticias[key] || key}
								</StatLabel>
							</Stat>
						),
					)}
				</SimpleGrid>
			</Box>
		</Box>
	);
};

export default EstadisticasPorAgencia;
